/**
 * PEPulse Admin-Cockpit — Ampel, Historie, Malus-Vorschau (nur Admin).
 */

import React, { useEffect, useMemo, useState } from 'react';
import useSocialMediaStore, { SocialMediaLog } from '../../store/socialMediaStore';
import {
    PACING_AMPEL_COLOR,
    SOCIAL_MEDIA_MONTHLY_TARGET_POINTS,
    achievementAmpel,
    achievementPercent,
    billingMalusRate,
    buildPepulseArchiveMonths,
    clampVacationDays,
    currentMonthKey,
    displayMalusRate,
    isBillingMalusExemptEmail,
    isMalusExemptEmail,
    isMartinaEmail,
    previousMonthKey,
    targetPointsFromVacation,
} from '../../utils/socialMediaScoring';
import { TeamMemberLike } from '../../utils/teamResolve';
import PepulseActivityLogModal from './PepulseActivityLogModal';

const formatPoints = (n: number) =>
    new Intl.NumberFormat('de-DE').format(Math.max(0, Math.round(n)));

const formatEuro = (n: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);

type Counts = { story: number; post: number; reel: number };

type DetailCoach = {
    id: string;
    name: string;
    email: string;
};

type Props = {
    pepulseTeam: TeamMemberLike[];
    /** Basis A Vorschau: optional map email -> Abrechnungssumme vor Malus (aktueller Monat) */
    payoutBaseByEmail?: Record<string, number>;
};

const PepulseAdminCockpit: React.FC<Props> = ({ pepulseTeam, payoutBaseByEmail = {} }) => {
    const fetchMonthAllLogs = useSocialMediaStore((s) => s.fetchMonthAllLogs);
    const fetchMonthMeta = useSocialMediaStore((s) => s.fetchMonthMeta);
    const isDeductionActive = useSocialMediaStore((s) => s.isDeductionActive);
    const setDeductionActive = useSocialMediaStore((s) => s.setDeductionActive);
    const ensureSettingsLoaded = useSocialMediaStore((s) => s.ensureSettingsLoaded);
    const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
    const [monthLogs, setMonthLogs] = useState<SocialMediaLog[]>([]);
    const [deductionBusy, setDeductionBusy] = useState(false);
    const [pointsByEmail, setPointsByEmail] = useState<Record<string, number>>({});
    const [countsByEmail, setCountsByEmail] = useState<Record<string, Counts>>({});
    const [vacationByEmail, setVacationByEmail] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(false);
    const [detailCoach, setDetailCoach] = useState<DetailCoach | null>(null);

    const monthOptions = useMemo(() => buildPepulseArchiveMonths(new Date()), []);

    useEffect(() => {
        ensureSettingsLoaded();
        return () => useSocialMediaStore.getState().unsubscribeSettings();
    }, [ensureSettingsLoaded]);

    useEffect(() => {
        // Falls Archiv-Start später als „aktuell“ (Edge): clamp
        if (monthOptions.length && !monthOptions.includes(selectedMonth)) {
            setSelectedMonth(monthOptions[0]);
        }
    }, [monthOptions, selectedMonth]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const [logs, meta] = await Promise.all([
                fetchMonthAllLogs(selectedMonth),
                fetchMonthMeta(selectedMonth),
            ]);
            if (cancelled) return;

            setMonthLogs(Array.isArray(logs) ? logs : []);

            const map: Record<string, number> = {};
            const counts: Record<string, Counts> = {};
            for (const log of logs) {
                const email = String(log.userEmail || '').trim().toLowerCase();
                if (!email) continue;
                map[email] = (map[email] || 0) + (Number(log.points) || 0);
                if (!counts[email]) counts[email] = { story: 0, post: 0, reel: 0 };
                const c = Math.max(1, Number(log.count) || 1);
                if (log.type === 'story') counts[email].story += c;
                else if (log.type === 'post') counts[email].post += c;
                else if (log.type === 'reel') counts[email].reel += c;
            }
            setPointsByEmail(map);
            setCountsByEmail(counts);

            const uidToEmail = new Map<string, string>();
            pepulseTeam.forEach((m) => {
                const uid = String(m.id || '').trim();
                const em = String(m.email || '').trim().toLowerCase();
                if (uid && em) uidToEmail.set(uid, em);
            });
            const vac: Record<string, number> = {};
            Object.values(meta).forEach((m) => {
                let email = String(m.userEmail || '').trim().toLowerCase();
                if (!email && m.userId) email = uidToEmail.get(String(m.userId)) || '';
                if (!email) return;
                vac[email] = clampVacationDays(m.vacationDays);
            });
            setVacationByEmail(vac);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedMonth, fetchMonthAllLogs, fetchMonthMeta, pepulseTeam]);

    const m1ForBilling = previousMonthKey(currentMonthKey());
    const isViewingBillingRelevant = selectedMonth === m1ForBilling;

    const openDetail = (row: { id: string; name: string; email: string }) => {
        setDetailCoach({
            id: row.id,
            name: row.name,
            email: row.email,
        });
    };

    const rows = useMemo(() => {
        return pepulseTeam
            .map((m) => {
                const email = String(m.email || '').trim().toLowerCase();
                const points = email ? pointsByEmail[email] || 0 : 0;
                const vacation = email ? vacationByEmail[email] || 0 : 0;
                const target = targetPointsFromVacation(vacation);
                const percent = achievementPercent(points, target);
                const displayRate = displayMalusRate(percent, email);
                const billingRate = billingMalusRate(percent, email);
                const flow = achievementAmpel(percent, displayRate);
                const base = email ? Number(payoutBaseByEmail[email] || 0) : 0;
                const malusEuro =
                    isDeductionActive &&
                    isViewingBillingRelevant &&
                    billingRate > 0 &&
                    base > 0
                        ? -(base * billingRate) / 100
                        : 0;
                const piece = email
                    ? countsByEmail[email] || { story: 0, post: 0, reel: 0 }
                    : { story: 0, post: 0, reel: 0 };
                return {
                    id: String(m.id || email || m.name),
                    name: String(m.name || 'Coach'),
                    email,
                    points,
                    target,
                    vacation,
                    percent,
                    displayRate,
                    billingRate,
                    flow,
                    exemptDisplay: isMalusExemptEmail(email),
                    billingExempt: isBillingMalusExemptEmail(email),
                    isMartina: isMartinaEmail(email),
                    malusEuro,
                    base,
                    stories: piece.story,
                    posts: piece.post,
                    reels: piece.reel,
                };
            })
            .sort((a, b) => b.points - a.points || a.name.localeCompare(b.name, 'de'));
    }, [
        pepulseTeam,
        pointsByEmail,
        countsByEmail,
        vacationByEmail,
        payoutBaseByEmail,
        isViewingBillingRelevant,
        isDeductionActive,
    ]);

    const handleDeductionToggle = async () => {
        if (deductionBusy) return;
        setDeductionBusy(true);
        try {
            await setDeductionActive(!isDeductionActive);
        } finally {
            setDeductionBusy(false);
        }
    };

    return (
        <div className="mt-3 rounded-xl border border-[#E6C299]/30 bg-[#0c0d11] px-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div>
                    <div className="text-sm font-bold text-[#E6C299]">Admin-Cockpit</div>
                    <div className="text-xs text-slate-500">
                        Ziel-Ampel · Stückzahlen · Urlaub · Malus (Abrechnung M-1 = {m1ForBilling})
                    </div>
                </div>
                <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="bg-[#15171e] border border-slate-700 text-[#E6C299] text-sm font-bold px-3 py-1.5 rounded outline-none"
                >
                    {monthOptions.map((m) => (
                        <option key={m} value={m}>
                            {m}
                            {m === m1ForBilling ? ' (Abrechnungs-Malus)' : ''}
                            {m === currentMonthKey() ? ' (aktuell)' : ''}
                        </option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p className="text-sm text-slate-500 py-2">Lade Monatsdaten…</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="text-slate-500 border-b border-slate-800 text-xs uppercase tracking-wider">
                                <th className="py-2 pr-2">Coach</th>
                                <th className="py-2 pr-2 text-right">S</th>
                                <th className="py-2 pr-2 text-right">B</th>
                                <th className="py-2 pr-2 text-right">R</th>
                                <th className="py-2 pr-2 text-right">Punkte</th>
                                <th className="py-2 pr-2 text-right">%</th>
                                <th className="py-2 pr-2 text-right">UI%</th>
                                <th className="py-2 pr-2 text-right">€%</th>
                                <th className="py-2 text-right">€ (M-1)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className="border-b border-slate-800/60 hover:bg-slate-900/40 transition-colors"
                                >
                                    <td className="py-1.5 pr-2">
                                        <span className="inline-flex items-center gap-2 flex-wrap">
                                            <span
                                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                                style={{ backgroundColor: PACING_AMPEL_COLOR[row.flow] }}
                                                title={`Ampel: ${row.flow}${
                                                    row.displayRate > 0
                                                        ? ` · Abzug ${row.displayRate}%`
                                                        : ''
                                                }`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => openDetail(row)}
                                                className="font-bold text-white hover:text-[#E6C299] transition-colors text-left"
                                                title="Aktivitäts-Protokoll öffnen"
                                            >
                                                {row.name}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openDetail(row)}
                                                className="inline-flex items-center justify-center w-6 h-6 rounded border border-slate-700 text-slate-500 hover:text-[#E6C299] hover:border-[#E6C299]/40 transition-colors"
                                                title="Aktivitäts-Protokoll"
                                                aria-label={`Protokoll für ${row.name}`}
                                            >
                                                <svg
                                                    width="12"
                                                    height="12"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden
                                                >
                                                    <circle cx="11" cy="11" r="7" />
                                                    <path d="M21 21l-4.3-4.3" />
                                                </svg>
                                            </button>
                                            {row.exemptDisplay ? (
                                                <span className="text-[10px] text-slate-500">exempt</span>
                                            ) : null}
                                            {row.isMartina ? (
                                                <span className="text-[10px] text-amber-500/80">0€ billing</span>
                                            ) : null}
                                            {row.vacation > 0 ? (
                                                <span className="text-[10px] text-slate-500">
                                                    {row.vacation}U
                                                </span>
                                            ) : null}
                                            {row.displayRate > 0 ? (
                                                <span className="text-[10px] font-bold text-red-400">
                                                    −{row.displayRate}%
                                                </span>
                                            ) : null}
                                        </span>
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-mono text-slate-300">
                                        {row.stories}
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-mono text-slate-300">
                                        {row.posts}
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-mono text-slate-300">
                                        {row.reels}
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-mono text-slate-200">
                                        {formatPoints(row.points)}
                                        <span className="text-slate-600">
                                            {' '}
                                            / {formatPoints(row.target || SOCIAL_MEDIA_MONTHLY_TARGET_POINTS)}
                                        </span>
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-mono text-slate-300">
                                        {Math.round(row.percent)}%
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-bold text-slate-200">
                                        {row.displayRate}%
                                    </td>
                                    <td className="py-1.5 pr-2 text-right font-bold text-slate-400">
                                        {row.billingRate}%
                                    </td>
                                    <td className="py-1.5 text-right font-mono text-red-400">
                                        {row.malusEuro < 0 ? formatEuro(row.malusEuro) : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="text-[10px] text-slate-600 mt-1.5">
                        S = Stories · B = Beiträge · R = Reels (Stückzahlen im gewählten Monat) · Name/Lupe =
                        Aktivitäts-Protokoll
                    </p>
                </div>
            )}

            <PepulseActivityLogModal
                isOpen={!!detailCoach}
                onClose={() => setDetailCoach(null)}
                coachName={detailCoach?.name || ''}
                coachEmail={detailCoach?.email || ''}
                coachUserId={detailCoach?.id || ''}
                month={selectedMonth}
                logs={monthLogs}
            />

            <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-200">
                        Monetären Abzug in Abrechnung aktivieren
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug max-w-xl">
                        Testphasen-Modus: Bei &apos;AUS&apos; bleibt die %-Anzeige sichtbar, aber es wird
                        kein Betrag von der Prämie abgezogen.
                    </p>
                    {!isDeductionActive && (
                        <span className="inline-block mt-1.5 text-[10px] font-black uppercase tracking-widest text-black bg-[#E6C299] px-2 py-0.5 rounded">
                            Testphase · Abzug AUS
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={isDeductionActive}
                    disabled={deductionBusy}
                    onClick={handleDeductionToggle}
                    className={`relative shrink-0 w-14 h-8 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-[#E6C299]/50 disabled:opacity-50 ${
                        isDeductionActive
                            ? 'bg-[#E6C299]/25 border-[#E6C299]'
                            : 'bg-[#15171e] border-slate-700'
                    }`}
                >
                    <span
                        className={`absolute top-1 left-1 w-6 h-6 rounded-full transition-transform shadow ${
                            isDeductionActive
                                ? 'translate-x-6 bg-[#E6C299]'
                                : 'translate-x-0 bg-slate-500'
                        }`}
                    />
                </button>
            </div>
        </div>
    );
};

export default PepulseAdminCockpit;
